/**
 * Integrytas Group - Data Archiving & Purging (DAP)
 * Page: DAP.POLICIES
 *
 * The retention dashboard: define policies (entity, action, conditions, batch
 * controls), DRY-RUN to see exact impacted volume, RUN throttled batches, and
 * ROLL BACK a snapshot within its window.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { useFormatDate } from '../../lib/format_date';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DAP.POLICIES';

registerUdcHost('DAP.POLICIES', 'dap_policy', 'Retention Policies');

type Action = 'COLD_STORAGE' | 'PURGE' | 'ANONYMIZE';
const OPS = ['GT', 'GE', 'LT', 'LE', 'EQ', 'NEQ', 'OLDER_THAN_DAYS', 'NEWER_THAN_DAYS', 'IS_NULL', 'NOT_NULL'];

const POLICY_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Code', required: true, col_span: 1 },
  { key: 'name', type: 'text', label: 'Name', col_span: 2 },
  { key: 'entity', type: 'text', label: 'Entity', col_span: 1 },
  { key: 'pk', type: 'text', label: 'PK Column', col_span: 1 },
  { key: 'action', type: 'select', label: 'Action', col_span: 1 },
  { key: 'mode', type: 'select', label: 'Conditions', col_span: 1 },
  { key: 'batch', type: 'number', label: 'Batch Size', col_span: 1 },
  { key: 'maxBatches', type: 'number', label: 'Max Batches/Run', col_span: 1 },
  { key: 'window', type: 'number', label: 'Rollback Window (days)', col_span: 1 },
];
const POLICY_CORE_LAYOUT = toUldCoreLayout(POLICY_FIELD_DEFS);

const POLICY_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',     defaultWidth: 220 },
  { key: 'entity_name',   header: 'Entity',   defaultWidth: 200 },
  { key: 'action',        header: 'Action',   defaultWidth: 140 },
  { key: 'is_active',     header: 'Active',   defaultWidth: 90  },
  { key: 'last_run_at',   header: 'Last Run', defaultWidth: 180 },
];

interface PolicyRow {
  dap_policy_id: number; internal_code: string; name: string; entity_name: string; pk_column: string;
  action: Action; condition_mode: 'STRUCTURED' | 'RAW'; raw_predicate: string | null; anonymize_columns: unknown;
  batch_size: number; max_batches_per_run: number; rollback_window_days: number; is_active: 'Y' | 'N'; last_run_at: string | null;
}
interface CondRow { dap_condition_id: number; column_name: string; operator: string; value: string | null; sort_order: number }
interface SnapRow { dap_snapshot_id: number; action: string; row_count: number; created_at: string; expires_at: string | null; is_restored: 'Y' | 'N' }

export interface DapPolicyManagerPageProps { session: AuthSession }

export function DapPolicyManagerPage({ session }: DapPolicyManagerPageProps) {
  return (
    <PersonalizationProvider pageCode="DAP.POLICIES" session={session}>
      <DapPolicyManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DapPolicyManagerPageInner({ session }: DapPolicyManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rows, setRows] = useState<PolicyRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PolicyRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PolicyRow | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ entity: string[]; action: string[]; active: string[] }>(
    { entity: [], action: [], active: ['Y'] },
  );

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('dap_policy')
        .select('dap_policy_id, internal_code, name, entity_name, pk_column, action, condition_mode, raw_predicate, anonymize_columns, batch_size, max_batches_per_run, rollback_window_days, is_active, last_run_at').order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as PolicyRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('dap_policy', 'DAP.POLICIES', { pkColumn: 'dap_policy_id', rows: rows as unknown as Record<string, unknown>[] });

  // TODO: wire relations — a policy's condition/snapshot child collections are loaded
  // only inside the edit modal, not on the list; surfacing them here would require a
  // new query (out of scope), so the disclosure renders inert (spacer).
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      entity: uniq(rows.map(r => r.entity_name)).map(v => ({ value: v, label: v })),
      action: uniq(rows.map(r => r.action)).map(v => ({ value: v, label: v })),
      active: [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }],
    };
  }, [rows, t]);

  const facetsActive = facets.entity.length + facets.action.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ entity: [], action: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matchesUdc = (r: PolicyRow) => udcGrid.matchRow(r as unknown as Record<string, unknown>);
    // OR within a facet, AND across facets.
    const matchesFacets = (r: PolicyRow) =>
      (facets.entity.length === 0 || facets.entity.includes(r.entity_name)) &&
      (facets.action.length === 0 || facets.action.includes(r.action)) &&
      (facets.active.length === 0 || facets.active.includes(r.is_active));
    const base = (!q ? rows : rows.filter(r =>
      r.internal_code.toLowerCase().includes(q) ||
      (r.name ?? '').toLowerCase().includes(q) ||
      (r.entity_name ?? '').toLowerCase().includes(q) ||
      r.action.toLowerCase().includes(q)));
    return base.filter(matchesUdc).filter(matchesFacets);
  }, [rows, filter, facets, udcGrid]);

  const handleDelete = async (r: PolicyRow): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'dap_policy', 'dap_policy_id', r.dap_policy_id);
      if (e) throw e;
      await reload();
      return true;
    } catch (e) { setError((e as Error).message); return false; } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('dap.title', 'Retention Policies')}
          description={t('dap.list.description', 'Define retention policies, simulate their impact, run throttled batches, and roll back snapshots.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('dap.refresh', 'Refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('dap.new_policy', 'New Policy')}
              </button>)}
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('dap.stat.total', 'Total policies') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('dap.stat.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('dap.load_failed', "Couldn't load retention policies.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('dap.filter_placeholder', 'Filter policies…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('dap.col.entity', 'Entity')} options={facetOptions.entity} selected={facets.entity} onChange={v => setFacets(f => ({ ...f, entity: v }))} />
            <FacetFilter label={t('dap.col.action', 'Action')} options={facetOptions.action} selected={facets.action} onChange={v => setFacets(f => ({ ...f, action: v }))} />
            <FacetFilter label={t('dap.col.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('dap.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...POLICY_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1" title={t('dap.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('dap.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.dap_policy_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.dap_policy_id)}
                        hasChildren={hasRelated(r.dap_policy_id)}
                        onToggle={() => detail.toggle(r.dap_policy_id)}
                        expandLabel={t('dap.related.expand', 'Show related records')}
                        collapseLabel={t('dap.related.collapse', 'Hide related')}
                        tooltip={t('dap.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>}
                        {col.key === 'entity_name'   && <span className="font-mono text-csm-xs">{r.entity_name}</span>}
                        {col.key === 'action'        && <span className="text-csm-xs">{r.action}</span>}
                        {col.key === 'is_active'     && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {col.key === 'last_run_at'   && <span className="text-csm-2xs text-csm-watermark">{r.last_run_at ? new Date(r.last_run_at).toLocaleString() : '—'}</span>}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right whitespace-nowrap">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('dap.action.edit', 'Edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('dap.action.delete', 'Delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.dap_policy_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.dap_policy_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState
                      variant={anyFilterActive ? 'no-match' : 'no-data'}
                      title={t('dap.empty', 'No retention policies')}
                      action={
                        canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                          {t('dap.new_policy_plus', '+ New Policy')}
                        </button> : undefined
                      }
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
      {(editing || creating) && (
        <PolicyModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('dap.delete.title', 'Delete Policy?')}
          message={t('dap.delete.message', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.name || deleteTarget.internal_code })}
          onConfirm={async () => { const ok = await handleDelete(deleteTarget); if (ok) setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function PolicyModal({ session, row, onClose, onSaved }: { session: AuthSession; row: PolicyRow | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const isCreate = row === null;
  const id = row?.dap_policy_id;

  const initialValues = {
    code: row?.internal_code ?? '',
    name: row?.name ?? '',
    entity: row?.entity_name ?? '',
    pk: row?.pk_column ?? 'id',
    action: (row?.action ?? 'COLD_STORAGE') as Action,
    mode: (row?.condition_mode ?? 'STRUCTURED') as 'STRUCTURED' | 'RAW',
    raw: row?.raw_predicate ?? '',
    anon: JSON.stringify(row?.anonymize_columns ?? {}),
    batch: row?.batch_size ?? 1000,
    maxBatches: row?.max_batches_per_run ?? 50,
    window: row?.rollback_window_days ?? 30,
    active: row ? row.is_active === 'Y' : false,
  };
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  // Custom fields placed on this page's form become first-class FieldRenderer items:
  // they interleave with native fields and inherit the same personalization.
  const udcForm = useUdcFormFields('dap_policy', 'DAP.POLICIES', { pkColumn: 'dap_policy_id', pk: row?.dap_policy_id });
  const policyCoreLayout = useMemo(() => [...POLICY_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const policyFieldDefs = useMemo(() => [...POLICY_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('DAP.POLICY.MODAL', policyCoreLayout);
  const [conds, setConds] = useState<CondRow[]>([]);
  const [snaps, setSnaps] = useState<SnapRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);
  const [runConfirm, setRunConfirm] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<number | null>(null);
  const set = (patch: Partial<typeof form>) => setForm(f => ({ ...f, ...patch }));

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.dap_policy_id, isCreate]);

  const loadChildren = async () => {
    if (!id) return;
    const [c, s] = await Promise.all([
      apiDb.from('dap_condition').select('dap_condition_id, column_name, operator, value, sort_order').eq('dap_policy_id', id).order('sort_order'),
      apiDb.from('dap_snapshot').select('dap_snapshot_id, action, row_count, created_at, expires_at, is_restored').eq('dap_policy_id', id).eq('is_restored', 'N').order('created_at', { ascending: false }),
    ]);
    setConds((c.data ?? []) as CondRow[]); setSnaps((s.data ?? []) as SnapRow[]);
  };
  // loadChildren is re-created each render; reload only when the selected id changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadChildren(); }, [id]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (isCreate && !form.code.trim()) throw new Error(t('dap.err.code_required', 'Code is required.'));
      let anon: unknown; try { anon = JSON.parse(form.anon || '{}'); } catch { throw new Error(t('dap.err.anon_json', 'Anonymize columns must be JSON, e.g. {"email":"HASH"}.')); }
      const payload = {
        name: form.name.trim(), entity_name: form.entity.trim(), pk_column: form.pk.trim() || 'id', action: form.action,
        condition_mode: form.mode, raw_predicate: form.raw || null, anonymize_columns: anon, batch_size: form.batch,
        max_batches_per_run: form.maxBatches, rollback_window_days: form.window, is_active: form.active ? 'Y' : 'N',
      };
      if (isCreate) { const { error: e } = await routedInsert(session, 'dap_policy', { internal_code: form.code.trim().toUpperCase(), ...payload }); if (e) throw e; }
      else { const { error: e } = await routedUpdate(session, 'dap_policy', 'dap_policy_id', id, payload); if (e) throw e; }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addCond = async () => { if (!id) return; await routedInsert(session, 'dap_condition', { dap_policy_id: id, column_name: '', operator: 'OLDER_THAN_DAYS', value: '180', sort_order: conds.length + 1 }); await loadChildren(); };
  const saveCond = async (c: CondRow) => { await routedUpdate(session, 'dap_condition', 'dap_condition_id', c.dap_condition_id, { column_name: c.column_name, operator: c.operator, value: c.value, sort_order: c.sort_order }); };
  const delCond = async (cid: number) => { await routedDelete(session, 'dap_condition', 'dap_condition_id', cid); setConds(cs => cs.filter(x => x.dap_condition_id !== cid)); };

  const dryRun = async () => {
    if (!row) return; setBusy(true); setError(null); setNotice(null);
    try {
      const { data, error: e } = await apiDb.rpc('dap_simulate', { p_policy_code: row.internal_code });
      if (e) throw e;
      const r = data as { ok: boolean; impacted_rows?: number; predicate?: string; error_code?: string };
      if (!r?.ok) throw new Error(r?.error_code ?? 'DAP.SIMULATE_FAILED');
      setNotice(t('dap.notice.dry_run', 'Dry run: {{rows}} row(s) would be {{action}}. Predicate: {{predicate}}', { rows: r.impacted_rows, action: row.action, predicate: r.predicate }));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const run = async () => {
    if (!row) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const { data, error: e } = await apiDb.rpc('dap_run_policy', { p_policy_code: row.internal_code });
      if (e) throw e;
      const r = data as { ok: boolean; rows_affected?: number; status?: string; snapshot_id?: number; error_code?: string };
      if (!r?.ok) throw new Error(r?.error_code ?? 'DAP.RUN_FAILED');
      setNotice(r.snapshot_id
        ? t('dap.notice.executed_snapshot', 'Executed: {{rows}} row(s) [{{status}}] — snapshot #{{snapshot}}.', { rows: r.rows_affected, status: r.status, snapshot: r.snapshot_id })
        : t('dap.notice.executed', 'Executed: {{rows}} row(s) [{{status}}].', { rows: r.rows_affected, status: r.status }));
      await loadChildren();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const rollback = async (sid: number) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { data, error: e } = await apiDb.rpc('dap_rollback', { p_snapshot_id: sid });
      if (e) throw e;
      const r = data as { ok: boolean; restored?: number; error_code?: string };
      if (!r?.ok) throw new Error(r?.error_code ?? 'DAP.ROLLBACK_FAILED');
      setNotice(t('dap.notice.rolled_back', 'Rolled back {{rows}} row(s).', { rows: r.restored }));
      await loadChildren();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const modalTitle = isCreate ? t('dap.modal.create_title', 'Create Retention Policy') : t('dap.modal.edit_title', 'Edit Policy — {{code}}', { code: row?.internal_code ?? '' });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        width="3xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('dap.close', 'Close')}</button>
        }
      >
        <div className="mb-3">
          <FieldRenderer
            fields={policyFieldDefs}
            layout={layout}
            columns={3}
            pageCode="DAP.POLICY.MODAL"
            session={session}
            render={(fld) => {
              if (fld.key === 'code') return (
                <PersonalizedField componentKey="modal" fieldKey="code" label={t('dap.field.code', 'Code')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.code', 'Code')}</label>
                  <input value={form.code} disabled={!isCreate}
                         onChange={e => set({ code: e.target.value.toUpperCase() })}
                         onBlur={() => {
                           if (isCreate && form.code && !/^[A-Z][A-Z0-9_.]*$/.test(form.code)) {
                             setInternalCodeError(t('dap.err.code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                           } else {
                             setInternalCodeError(null);
                           }
                         }}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : isCreate && <p className="text-csm-xs text-csm-ink-soft mt-1">{t('dap.hint.code', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                  }
                </PersonalizedField>
              );
              if (fld.key === 'name') return (
                <PersonalizedField componentKey="modal" fieldKey="name" label={t('dap.field.name', 'Name')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.name', 'Name')}</label>
                  <input value={form.name} onChange={e => set({ name: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></PersonalizedField>
              );
              if (fld.key === 'entity') return (
                <PersonalizedField componentKey="modal" fieldKey="entity" label={t('dap.field.entity', 'Entity')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.entity', 'Entity')}</label>
                  <input value={form.entity} onChange={e => set({ entity: e.target.value })} placeholder="csm_audit_log" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></PersonalizedField>
              );
              if (fld.key === 'pk') return (
                <PersonalizedField componentKey="modal" fieldKey="pk" label={t('dap.field.pk_column', 'PK Column')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.pk_column', 'PK Column')}</label>
                  <input value={form.pk} onChange={e => set({ pk: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></PersonalizedField>
              );
              if (fld.key === 'action') return (
                <PersonalizedField componentKey="modal" fieldKey="action" label={t('dap.field.action', 'Action')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.action', 'Action')}</label>
                  <select value={form.action} onChange={e => set({ action: e.target.value as Action })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">{['COLD_STORAGE', 'PURGE', 'ANONYMIZE'].map(a => <option key={a} value={a}>{a}</option>)}</select></PersonalizedField>
              );
              if (fld.key === 'mode') return (
                <PersonalizedField componentKey="modal" fieldKey="mode" label={t('dap.field.conditions', 'Conditions')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.conditions', 'Conditions')}</label>
                  <select value={form.mode} onChange={e => set({ mode: e.target.value as 'STRUCTURED' | 'RAW' })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm"><option value="STRUCTURED">{t('dap.mode.structured', 'Structured')}</option><option value="RAW">{t('dap.mode.raw_sql', 'Raw SQL')}</option></select></PersonalizedField>
              );
              if (fld.key === 'batch') return (
                <PersonalizedField componentKey="modal" fieldKey="batch" label={t('dap.field.batch_size', 'Batch Size')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.batch_size', 'Batch Size')}</label>
                  <input type="number" value={form.batch} onChange={e => set({ batch: Number(e.target.value) })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></PersonalizedField>
              );
              if (fld.key === 'maxBatches') return (
                <PersonalizedField componentKey="modal" fieldKey="maxBatches" label={t('dap.field.max_batches', 'Max Batches/Run')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.max_batches', 'Max Batches/Run')}</label>
                  <input type="number" value={form.maxBatches} onChange={e => set({ maxBatches: Number(e.target.value) })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></PersonalizedField>
              );
              if (fld.key === 'window') return (
                <PersonalizedField componentKey="modal" fieldKey="window" label={t('dap.field.rollback_window', 'Rollback Window (days)')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.field.rollback_window', 'Rollback Window (days)')}</label>
                  <input type="number" value={form.window} onChange={e => set({ window: Number(e.target.value) })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></PersonalizedField>
              );
              if (udcForm.isUdcKey(fld.key)) return (
                <PersonalizedField componentKey="modal" fieldKey={fld.key} label={fld.label}
                  readonlyChildren={udcForm.renderControl(fld.key, { disabled: true })}>
                  {udcForm.renderControl(fld.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>

        {form.mode === 'RAW'
          ? <div className="mb-3"><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.label.raw_predicate', 'Raw WHERE predicate')}</label>
              <textarea rows={2} value={form.raw} onChange={e => set({ raw: e.target.value })} placeholder="status = 'CLOSED' AND updated_at &lt; now() - interval '1 year'" className="w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs" /></div>
          : !isCreate && (
            <div className="mb-3 rounded border border-csm-border-soft p-2">
              <div className="mb-1 flex items-center gap-2"><span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('dap.section.conditions', 'Conditions (AND)')}</span>
                <button onClick={() => void addCond()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('dap.add_condition', '+ Condition')}</button></div>
              {conds.map((c, i) => (
                <div key={c.dap_condition_id} className="mb-1 flex flex-wrap items-center gap-1">
                  <input value={c.column_name} onChange={e => setConds(cs => cs.map((x, j) => j === i ? { ...x, column_name: e.target.value } : x))} onBlur={() => void saveCond(conds[i])} placeholder={t('dap.placeholder.column', 'column')} className="h-7 w-36 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <select value={c.operator} onChange={e => setConds(cs => cs.map((x, j) => j === i ? { ...x, operator: e.target.value } : x))} onBlur={() => void saveCond(conds[i])} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{OPS.map(o => <option key={o} value={o}>{o}</option>)}</select>
                  {!['IS_NULL', 'NOT_NULL'].includes(c.operator) && <input value={c.value ?? ''} onChange={e => setConds(cs => cs.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} onBlur={() => void saveCond(conds[i])} placeholder={t('dap.placeholder.value', 'value')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />}
                  <button onClick={() => void delCond(c.dap_condition_id)} className="text-csm-danger text-csm-xs">{t('dap.del', 'del')}</button>
                </div>
              ))}
            </div>
          )}

        {form.action === 'ANONYMIZE' && (
          <div className="mb-3"><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dap.label.anonymize_columns', 'Anonymize columns (JSON)')}</label>
            <input value={form.anon} onChange={e => set({ anon: e.target.value })} placeholder='{"email":"HASH","phone":"NULL"}' className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-xs" /></div>
        )}

        <div className="mb-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={form.active} onChange={e => set({ active: e.target.checked })} className="h-4 w-4 accent-gray-700" /> {t('dap.active', 'Active')}</label>
          <button onClick={() => void save()} disabled={busy} className="ml-auto h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('dap.saving', 'Saving…') : t('dap.save_policy', 'Save Policy')}</button>
        </div>

        {!isCreate && (
          <div className="border-t border-csm-border pt-2">
            <div className="mb-2 flex flex-wrap gap-2">
              <button onClick={() => void dryRun()} disabled={busy} className="h-8 rounded border border-csm-border bg-white px-3 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-60">{t('dap.dry_run', 'Dry Run (simulate)')}</button>
              <button onClick={() => setRunConfirm(true)} disabled={busy} className="h-8 rounded border border-csm-danger-border bg-csm-danger-soft px-3 text-csm-sm text-csm-danger hover:opacity-80 disabled:opacity-60">{t('dap.execute_now', 'Execute Now')}</button>
            </div>
            {notice && <div className="mb-2 rounded border border-csm-border bg-csm-surface-alt px-2 py-1 text-csm-xs text-csm-ink-soft">{notice}</div>}
            <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('dap.section.snapshots', 'Restorable Snapshots')}</div>
            {snaps.length === 0 && <div className="text-csm-2xs text-csm-watermark">{t('dap.snapshots.none', 'None.')}</div>}
            {snaps.map(s => (
              <div key={s.dap_snapshot_id} className="mb-1 flex items-center gap-2 text-csm-xs">
                <span className="font-mono">#{s.dap_snapshot_id}</span><span>{s.action}</span><span>{t('dap.snapshot.rows', '{{count}} rows', { count: s.row_count })}</span>
                <span className="text-csm-2xs text-csm-watermark">{t('dap.snapshot.exp', 'exp {{date}}', { date: s.expires_at ? formatDate(s.expires_at) : '—' })}</span>
                <button onClick={() => setRollbackTarget(s.dap_snapshot_id)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-2xs hover:bg-csm-surface-alt">{t('dap.roll_back', 'Roll back')}</button>
              </div>
            ))}
          </div>
        )}

        {error &&<div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('dap.hint.save_first', 'Save first to add conditions and run dry-run / execute.')}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('dap.discard.title', 'Discard changes?')}
          message={t('dap.discard.message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('dap.discard.confirm', 'Discard')}
          cancelLabel={t('dap.discard.cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}

      {runConfirm && row && (
        <ConfirmModal
          title={t('dap.run.title', 'Execute {{action}}?', { action: row.action })}
          message={(row.action === 'PURGE'
            ? t('dap.run.message_purge', 'Execute {{action}} on {{entity}}? This processes throttled batches now. Rows are hard-deleted (rollback snapshot kept).', { action: row.action, entity: row.entity_name })
            : t('dap.run.message', 'Execute {{action}} on {{entity}}? This processes throttled batches now.', { action: row.action, entity: row.entity_name }))}
          confirmLabel={t('dap.run.confirm', 'Execute')}
          cancelLabel={t('dap.run.cancel', 'Cancel')}
          danger={true}
          onConfirm={async () => { setRunConfirm(false); await run(); }}
          onCancel={() => setRunConfirm(false)}
        />
      )}

      {rollbackTarget !== null && (
        <ConfirmModal
          title={t('dap.rollback.title', 'Roll Back Snapshot?')}
          message={t('dap.rollback.message', 'Restore snapshot #{{id}}? This will overwrite current rows with the archived data. This cannot be undone.', { id: rollbackTarget })}
          confirmLabel={t('dap.rollback.confirm', 'Roll Back')}
          cancelLabel={t('dap.rollback.cancel', 'Cancel')}
          danger={true}
          onConfirm={async () => { const sid = rollbackTarget; setRollbackTarget(null); await rollback(sid); }}
          onCancel={() => setRollbackTarget(null)}
        />
      )}
    </>
  );
}
